<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, inject, onErrorCaptured } from 'vue';
import type { WritableComputedRef } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import type { AddressInfo } from '../types/contact.types';
import { contactDetailMyResidentialEditingKey } from '../contactDetailInjectionKeys';
import {
    applyCountryChangeClearsSubLocation,
    buildMyLocalAddressRecordFromForm,
    MY_MALAYSIA_COUNTRY_CODE,
    resolveTotalCodeFromField,
    resolveCountryTotalCode,
    countryCodeToSearchLabel,
    shouldShowMyLocationFieldsWithAddDefault,
    normalizeMyLocationRowForForm,
    normalizeMyLocationFieldForPersist,
    resolvePostcodeDisplayNameFromOptions,
    resolvePostcodeTotalCodeForForm,
    isMyLocationGuidLike,
    clearMyLocationPlaceholdersOnAddress,
    sanitizeMyLocationValueForForm,
    isMyLocationPlaceholderValue,
    formatMyAddressRowBodyForDisplay,
    formatMyResidentialSummaryForDisplay,
    isMyCorporateBranchAddressEntityMapped,
    ensureMalaysiaCountryWhenMyLocationPresent,
} from '../utils/contactMyLocationFields';
import ContactAddressSelectDialog from './ContactAddressSelectDialog.vue';
import ContactBranchAddressShowDialog from './ContactBranchAddressShowDialog.vue';
import MyBranchAddressDisplayLine from './MyBranchAddressDisplayLine.vue';
import ContactResidentialBranchRowEditor from './ContactResidentialBranchRowEditor.vue';
import ContactAddressConsolidateDialog from './ContactAddressConsolidateDialog.vue';
import type { ContactAddressCandidate } from '../types/contact.types';
import { mapCandidateToMyAddressData } from '../utils/contactAddressSelect';
import {
    applyBranchAddressRowToDraft,
    buildAllCorporateBranchShowItems,
    type BranchAddressShowItem,
} from '../utils/contactBranchAddressShow';
import {
    ensureCachedMyCountryAndState,
    fetchCachedMyAllPostcodeOptions,
} from '../utils/myLocationOptionsCache';
import { useMyCorporateBranchAddressDelete } from '../composables/useMyCorporateBranchAddressDelete';
import type { MyRegisterAddressDeleteContext } from '@/features/register/MY/utils/persistMyRegisterContactAddresses';
import {
    shouldPersistMyAddressDelete,
    type MyCorporateBranchAddressLists,
} from '../utils/persistMyCorporateBranchAddresses';

type AddressType = 'B07001' | 'B07002'; // 'B07001' = Local Address, 'B07002' = Foreign Address

interface AddressModel {
    addressType?: AddressType | null;
    localAddress?: AddressInfo | null;
    foreignAddress?: AddressInfo | null;
    [key: string]: any;
}

interface Props {
    modelValue?: AddressModel | null;
    readonly?: boolean;
    isIndividual?: boolean; // Individual Detail인지 여부
    contactCd?: string;
    entityCd?: string;
    useBranchAddresses?: boolean;
    residentialAddressList?: any[];
    residentialAddressShowPool?: any[];
    serviceAddressListForShow?: any[];
    businessAddressListForShow?: any[];
    showResidentialMultiHint?: boolean;
    residentialMultiHintText?: string;
    onReSyncAddressClick?: () => void;
    resolveBranchListsForPersist?: (residentialList: any[]) => MyCorporateBranchAddressLists;
    /** Contact List / Registrable — Residential·Registered 대신 "Address" 라벨 */
    simpleAddressLabel?: boolean;
    /** Contact List / Registrable — 다건 힌트·Select Address·Address Consolidation 숨김 */
    hideAddressConsolidationUi?: boolean;
    /** Register BO 등 — 주소 행 삭제 버튼 숨김 */
    hideAddressDeleteButton?: boolean;
    contactListFlatAddressMode?: boolean;
    /** Entity Detail — branch 주소 Delete(−) 숨김 */
    hideAddressDelete?: boolean;
    /** MY Register — 역할별 주소 삭제 시 Register API 사용 */
    registerAddressDeleteContext?: MyRegisterAddressDeleteContext | null;
}

const props = withDefaults(defineProps<Props>(), {
    readonly: false,
    isIndividual: false,
    useBranchAddresses: false,
    residentialAddressList: () => [],
    residentialAddressShowPool: () => [],
    serviceAddressListForShow: () => [],
    businessAddressListForShow: () => [],
    showResidentialMultiHint: false,
    residentialMultiHintText: 'Multiple residential addresses exist for this contact.',
    simpleAddressLabel: false,
    hideAddressConsolidationUi: false,
    hideAddressDeleteButton: false,
    contactListFlatAddressMode: false,
    hideAddressDelete: false,
    registerAddressDeleteContext: null,
});

const emit = defineEmits<{
    'update:modelValue': [value: AddressModel];
    'update:residentialAddressList': [value: any[]];
    refresh: [];
}>();

defineOptions({ inheritAttrs: false });

const { isDeleting, confirmAndDeleteBranchAddress } = useMyCorporateBranchAddressDelete({
    getContactCd: () => String(props.contactCd || '').trim(),
    getRegisterAddressDeleteContext: () => props.registerAddressDeleteContext ?? null,
    getContactListFlatAddressMode: () => !!props.contactListFlatAddressMode,
    onRefreshed: async () => {
        emit('refresh');
    },
});

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
const residentialAddressLabel = computed(() => {
    if (props.simpleAddressLabel) {
        return t('contact.Address') || 'Address';
    }
    return props.isIndividual
        ? (t('contact.Residential Address') || 'Residential Address')
        : (t('contact.Registered Address') || 'Registered Address');
});
const showResidentialMultiHintEffective = computed(
    () => props.showResidentialMultiHint && !props.hideAddressConsolidationUi,
);
const isComponentAlive = ref(true);

const upsertOption = (
  source: Array<{ TOTAL_CODE: string; CODE_NAME: string }>,
  nextOption: { TOTAL_CODE: string; CODE_NAME: string }
) => {
  const totalCode = String(nextOption.TOTAL_CODE || '').trim();
  if (!totalCode) return source;

  const normalized = {
    TOTAL_CODE: totalCode,
    CODE_NAME: String(nextOption.CODE_NAME || totalCode).trim()
  };

  const existingIndex = source.findIndex((opt) => opt.TOTAL_CODE === totalCode);
  if (existingIndex === -1) {
    return [...source, normalized];
  }

  const clone = [...source];
  clone[existingIndex] = { ...clone[existingIndex], ...normalized };
  return clone;
};

// 세션의 JURISDICTION이 MY인지 확인
const sessionStore = useSessionStore();
const isJurisdictionMY = computed(() => {
  // sessionStore.user를 직접 참조하여 반응형으로 작동하도록 수정
  const customerInfo = sessionStore.user?.customer || null;
  const jurisdiction = customerInfo?.jurisdiction || '';
  const isMY = jurisdiction?.toUpperCase() === 'MY' || jurisdiction?.toUpperCase() === 'MALAYSIA';
  return isMY;
});

/** MY location 옵션 — branch watch(immediate)·computed보다 먼저 선언 (TDZ 방지) */
const countryOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const stateOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const cityOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
const postcodeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);
/** 전국 Postcode 캐시 — City별 목록과 병합·역검색용 */
const postcodeAllOptionsCache = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

const mergedResidentialPostcodeAutocompleteItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    postcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    postcodeOptions.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    return Array.from(map.values());
});

const residentialPostcodeReverseSearchItems = computed(() => {
    const map = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    postcodeAllOptionsCache.value.forEach((o) => map.set(o.TOTAL_CODE, o));
    return Array.from(map.values());
});

function getResidentialPostcodeComboItems(): Array<{ TOTAL_CODE: string; CODE_NAME: string }> {
    const cityRaw = myAddressData.value.city;
    const cityCode =
        resolveTotalCodeFromField(cityRaw, cityOptions.value) || String(cityRaw || '').trim();
    if (cityCode) {
        return postcodeOptions.value.length > 0
            ? postcodeOptions.value
            : mergedResidentialPostcodeAutocompleteItems.value;
    }
    return residentialPostcodeReverseSearchItems.value;
}

async function ensureResidentialPostcodeFullCacheForReverse() {
    if (postcodeAllOptionsCache.value.length > 0) return;
    await loadAllPostcodeOptions();
}

/** MY Corporate branch Registered — ContactInfoForm Service/Business와 동일 콤보 규칙 */
function getBranchResidentialPostcodeComboItems(address: any) {
    const cityRaw = address?.city;
    const cityCode =
        resolveTotalCodeFromField(cityRaw, cityOptions.value) || String(cityRaw || '').trim();
    let items: Array<{ TOTAL_CODE: string; CODE_NAME: string }>;
    if (cityCode) {
        items =
            postcodeOptions.value.length > 0
                ? postcodeOptions.value
                : mergedResidentialPostcodeAutocompleteItems.value;
    } else {
        items = residentialPostcodeReverseSearchItems.value;
    }
    const current = String(address?.post_code ?? '').trim();
    if (!current || items.some((o) => o.TOTAL_CODE === current)) {
        return items;
    }
    const fromMerged = mergedResidentialPostcodeAutocompleteItems.value.find(
        (o) => o.TOTAL_CODE === current || o.CODE_NAME === current,
    );
    return fromMerged ? [...items, fromMerged] : items;
}

async function hydrateResidentialMyAddressFields() {
    if (!isJurisdictionMY.value) return;
    const localAddr = addressData.value.localAddress;
    if (!localAddr) return;
    isInitializingLocation.value = true;
    try {
        await bindResidentialMyAddressDataFromLocal(localAddr as Record<string, unknown>);
        syncResidentialCountrySearchFromModel();
        syncResidentialCountrySnapshotFromMyData();
    } finally {
        await nextTick();
        isInitializingLocation.value = false;
    }
}

const isLoadingCities = ref(false);
const isLoadingPostcodes = ref(false);
const isInitializingLocation = ref(false);
const suppressLocationCascade = ref(false);
const isResettingLocationFromCountryChange = ref(false);
const residentialCountrySnapshot = ref('');

const loadCountryOptions = async () => {
  if (countryOptions.value.length > 0) return;
  try {
    const { countries } = await ensureCachedMyCountryAndState();
    countryOptions.value = countries;
  } catch (error) {
    console.error('❌ [loadCountryOptions] Country 로드 실패:', error);
    countryOptions.value = [];
  }
};

const loadStateOptions = async () => {
  if (stateOptions.value.length > 0) return;
  try {
    const { states } = await ensureCachedMyCountryAndState();
    stateOptions.value = states;
  } catch (error) {
    console.error('❌ [loadStateOptions] State 로드 실패:', error);
    stateOptions.value = [];
  }
};

let citiesLoadSeq = 0;
/** 현재 cityOptions가 어떤 State GUID 기준인지 */
const cityOptionsForStateCode = ref('');
/** City별 Postcode 목록 추적 — loadPostcodeOptions보다 먼저 선언 (TDZ 방지) */
const postcodeOptionsForCityCode = ref('');
const loadCitiesByState = async (stateCode: string | null) => {
  if (!stateCode) {
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
    return;
  }

  const resolvedState =
    resolveTotalCodeFromField(stateCode, stateOptions.value) || String(stateCode).trim();
  if (!resolvedState || isMyLocationPlaceholderValue(resolvedState)) {
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
    return;
  }

  const seq = ++citiesLoadSeq;
  isLoadingCities.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('City 목록 로드 타임아웃')), 5000)
    );

    const response = await Promise.race([
      entityMyApi.getMalaysiaCities(resolvedState, undefined, false),
      timeoutPromise,
    ]) as any;

    if (seq !== citiesLoadSeq) return;

    const cities = response.data?.data || response.data || [];

    cityOptions.value = cities
      .map((city: any) => ({
        TOTAL_CODE: String(city.code || '').trim(),
        CODE_NAME: String(city.name || '').trim(),
      }))
      .filter((item: any) => item.TOTAL_CODE && item.CODE_NAME);
    cityOptionsForStateCode.value = resolvedState.toLowerCase();
  } catch (error) {
    if (seq !== citiesLoadSeq) return;
    console.error('❌ [loadCitiesByState] City 목록 로드 실패:', error);
    cityOptions.value = [];
    cityOptionsForStateCode.value = '';
  } finally {
    if (seq === citiesLoadSeq) {
      isLoadingCities.value = false;
    }
  }
};

async function ensureCitiesLoadedForState(stateRaw: unknown) {
  const state = String(stateRaw ?? '').trim();
  if (!state || isMyLocationPlaceholderValue(state)) return;
  const resolved =
    resolveTotalCodeFromField(state, stateOptions.value) || state;
  const resolvedKey = resolved.toLowerCase();
  if (
    cityOptions.value.length > 0 &&
    cityOptionsForStateCode.value &&
    cityOptionsForStateCode.value === resolvedKey
  ) {
    return;
  }
  await loadCitiesByState(state);
}

async function ensureCitiesLoadedForCurrentState() {
  await ensureCitiesLoadedForState(myAddressData.value.state);
}

async function ensureCitiesLoadedForBranchRow(address: Record<string, unknown> | null | undefined) {
  if (!address) return;
  await ensureCitiesLoadedForState(address.state);
}

const loadAllPostcodeOptions = async () => {
  if (isLoadingPostcodes.value) {
    return;
  }
  if (postcodeAllOptionsCache.value.length > 0) {
    return;
  }

  isLoadingPostcodes.value = true;
  try {
    postcodeAllOptionsCache.value = await fetchCachedMyAllPostcodeOptions();
  } catch (error) {
    console.error('❌ [loadAllPostcodeOptions] 전체 Postcode 로드 실패:', error);
    postcodeAllOptionsCache.value = [];
  } finally {
    isLoadingPostcodes.value = false;
  }
};

onBeforeUnmount(() => {
    isComponentAlive.value = false;
});

// MY 옵션 로드 함수
const loadMyOptions = async () => {
    if (!isComponentAlive.value) return;

    // 비-readonly: 여기서 일괄 옵션 로드. readonly는 아래 분기에서 표시용으로 로드
    if (!readonly.value) {
        await loadCountryOptions();
        await loadStateOptions();
    }
    
    // addressData에서 초기값 설정
    if (addressData.value.localAddress) {
        // readonly: 분기 유지 — 표시용 옵션 로드 + 바인딩은 countryCode 등 우선
        if (readonly.value) {
            const localAddr = addressData.value.localAddress;
            await loadCountryOptions();
            await loadStateOptions();
            const la = localAddr as any;
            const countryCode = la.countryCode || localAddr.country || '';
            const stateCode = la.stateCode || localAddr.state || '';
            const cityCode = la.cityCode || localAddr.city || '';
            const postcodeCode =
                la.postcodeCode || localAddr.post_code || localAddr.postcode || localAddr.postalCode || '';
            if (stateCode) {
                await loadCitiesByState(stateCode);
            }
            if (cityCode && String(cityCode).trim()) {
                await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
            }
            myAddressData.value = {
                addressLine1: localAddr.addressLine1 || '',
                addressLine2: localAddr.addressLine2 || '',
                addressLine3: localAddr.addressLine3 || '',
                country: countryCode,
                state: stateCode,
                city: cityCode,
                postcode: postcodeCode,
            };
        } else {
            // readonly가 아닐 때는 옵션 로드 및 순차 로직 수행
            const localAddr = addressData.value.localAddress;
            
            // A06002 (Manual)인지 확인
            const registerType = (props.modelValue as any)?.CONTACT_REGISTER_TYPE || (props.modelValue as any)?.contactRegisterType || '';
            const isManual = String(registerType).trim().toUpperCase() === 'A06002';
            
            await loadAllPostcodeOptions();
            
            // 백엔드에서 역추적한 GUID 값을 우선 사용
            let countryCode = (localAddr as any).countryCode || '';
            let stateCode = (localAddr as any).stateCode || '';
            let cityCode = (localAddr as any).cityCode || '';
            
            // GUID 값이 없으면 텍스트 값을 사용 (역추적을 위해)
            const countryName = countryCode ? '' : (localAddr.country || '');
            const stateName = stateCode ? '' : (localAddr.state || '');
            const cityName = cityCode ? '' : (localAddr.city || '');
            
            if (isManual) {
                // A06002일 때: CODE_NAME → TOTAL_CODE 변환
                // Country: B15 공통코드에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (countryName && !countryCode) {
                    const countryMatch = countryOptions.value.find(opt => 
                        opt.CODE_NAME === countryName || opt.CODE_NAME?.toUpperCase() === String(countryName).toUpperCase()
                    );
                    if (countryMatch) {
                        countryCode = countryMatch.TOTAL_CODE;
                    }
                }
                
                // State: stateOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (stateName && !stateCode) {
                    const stateMatch = stateOptions.value.find(opt => 
                        opt.CODE_NAME === stateName || opt.CODE_NAME?.toUpperCase() === String(stateName).toUpperCase()
                    );
                    if (stateMatch) {
                        stateCode = stateMatch.TOTAL_CODE;
                        await loadCitiesByState(stateCode);
                    }
                }
                
                // City: cityOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (cityName && !cityCode) {
                    const cityMatch = cityOptions.value.find(opt => 
                        opt.CODE_NAME === cityName || opt.CODE_NAME?.toUpperCase() === String(cityName).toUpperCase()
                    );
                    if (cityMatch) {
                        cityCode = cityMatch.TOTAL_CODE;
                        await loadPostcodeOptions(cityCode, true).catch(() => {});
                    }
                }
                
                // Postcode: postcodeOptions에서 CODE_NAME으로 TOTAL_CODE 찾기
                if (cityCode) {
                    if (postcodeOptions.value.length === 0) {
                        await loadPostcodeOptions(cityCode, true).catch(() => {});
                    }
                }
            } else {
                countryCode = resolveTotalCodeFromField(
                    (localAddr as any).countryCode || localAddr.country,
                    countryOptions.value
                );
                stateCode = resolveTotalCodeFromField(
                    (localAddr as any).stateCode || localAddr.state,
                    stateOptions.value
                );
                cityCode = resolveTotalCodeFromField(
                    (localAddr as any).cityCode || localAddr.city,
                    cityOptions.value
                );
            }

            // Country가 있으면 countryOptions에 추가
            if (countryCode) {
                const countryExists = countryOptions.value.some(opt => opt.TOTAL_CODE === countryCode);
                if (!countryExists) {
                    countryOptions.value = upsertOption(countryOptions.value, {
                        TOTAL_CODE: String(countryCode).trim(),
                        CODE_NAME: String(localAddr.country || countryCode).trim().toUpperCase()
                    });
                }
            }

            // State가 있으면 City 목록 로드
            if (stateCode && !isManual) {
                await loadCitiesByState(stateCode);
            }
            
            // City가 있으면 City 옵션에 추가 및 Postcode 목록 로드
            if (cityCode && cityCode.trim()) {
                const cityExists = cityOptions.value.some(opt => opt.TOTAL_CODE === cityCode);
                if (!cityExists) {
                    cityOptions.value = upsertOption(cityOptions.value, {
                        TOTAL_CODE: String(cityCode).trim(),
                        CODE_NAME: String(localAddr.city || cityCode).trim()
                    });
                    entityMyApi.getMalaysiaCities(undefined, cityCode)
                        .then((cityResponse: any) => {
                            if (!isComponentAlive.value) return;
                            const cityData = cityResponse.data?.data || cityResponse.data;
                            const cityInfo = Array.isArray(cityData) ? cityData[0] : (cityData || {});
                            if (cityInfo?.name) {
                                const cityIndex = cityOptions.value.findIndex(opt => opt.TOTAL_CODE === cityCode);
                                if (cityIndex >= 0) {
                                    const nextCities = [...cityOptions.value];
                                    nextCities[cityIndex] = {
                                      ...nextCities[cityIndex],
                                      CODE_NAME: String(cityInfo.name).trim()
                                    };
                                    cityOptions.value = nextCities;
                                }
                            }
                        })
                        .catch(() => {});
                }
                await loadPostcodeOptions(cityCode, true).catch(() => {});
            }

            const postcodeCode = await bindResidentialPostcodeFromLocal(
                localAddr as Record<string, unknown>,
                cityCode,
            );
            
            // 초기 로드 플래그 설정
            isInitializingLocation.value = true;
            
            // street01, street02, street03를 addressLine1, addressLine2, addressLine3로 매핑
            const addressLine1 = localAddr.addressLine1 || (localAddr as any).street01 || '';
            const addressLine2 = localAddr.addressLine2 || (localAddr as any).street02 || '';
            const addressLine3 = localAddr.addressLine3 || (localAddr as any).street03 || '';
            
            if (!isComponentAlive.value) return;
            myAddressData.value = {
                addressLine1: addressLine1,
                addressLine2: addressLine2,
                addressLine3: addressLine3,
                country: countryCode, // GUID 값만 사용
                state: stateCode, // GUID 값만 사용
                city: cityCode, // GUID 값만 사용
                postcode: postcodeCode, // GUID 값만 사용
            };
            
            // DOM 업데이트 대기
            await nextTick();
            
            // 초기 로드 완료 후 플래그 해제
            await nextTick();
            setTimeout(() => {
                if (!isComponentAlive.value) return;
                isInitializingLocation.value = false;
            }, 200);
        }
    }
};

// readonly 변수 정의 (템플릿에서 사용)
const readonly = computed(() => props.readonly);
// MY인 경우 Entity 스타일 주소 데이터 (TDZ 방지를 위해 선행 선언)
const myAddressData = ref({
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    country: '',
    state: '',
    city: '',
    postcode: '',
});
const isMyAddressEditing =
    inject(contactDetailMyResidentialEditingKey, undefined) ?? ref(false);
/** MY + 주소 없음: Business Address처럼 + Add 로 블록 열기 전까지 필드 숨김 */
const residentialMyExpanded = ref(false);
/** + Add 직후 country 선택 전 State/City/Postcode 기본 표시 */
const residentialShowLocationOnAdd = ref(false);
const canEditMyAddress = computed(
    () =>
        (isJurisdictionMY.value || props.useBranchAddresses || props.contactListFlatAddressMode) &&
        !readonly.value,
);

const useMultiResidentialListUi = computed(
    () => !!props.useBranchAddresses || !!props.contactListFlatAddressMode,
);

const showMyCorporateBranchAddressUi = computed(
    () => isJurisdictionMY.value || useMultiResidentialListUi.value,
);

/** MY Contact List — Address 섹션 접기 미사용(항상 표시, 고객 요청) */
const useContactListAddressSectionCollapse = computed(() => false);
const addressSectionExpanded = ref(false);
const isAddressSectionCollapsed = computed(
    () => useContactListAddressSectionCollapse.value && !addressSectionExpanded.value,
);

watch(
    () => props.contactCd,
    () => {
        addressSectionExpanded.value = false;
    },
);

function toggleAddressSectionExpanded() {
    if (!useContactListAddressSectionCollapse.value) return;
    addressSectionExpanded.value = !addressSectionExpanded.value;
}

const formatBranchResidentialDisplay = (address: any): string =>
    formatMyAddressRowBodyForDisplay(address, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
    });

const branchRowEditorLabels = computed(() => ({
    showAddress: t('contact.Show Address') || 'Show Address',
    edit: t('contact.Edit') || 'Edit',
    cancel: t('contact.Cancel') || 'Cancel',
    delete: t('contact.Delete') || 'Delete',
    alias: t('contact.Alias') || 'Alias',
    line1: t('contact.Address Line 1') || 'Address Line 1',
    line2: t('contact.Address Line 2') || 'Address Line 2',
    line3: t('contact.Address Line 3') || 'Address Line 3',
    country: t('contact.Country') || 'Country',
    state: t('contact.State') || 'State',
    city: t('contact.City') || 'City',
    postcode: t('contact.Postcode') || 'Postcode',
    pleaseSelect: t('contact.Please Select') || 'Please Select',
}));

const residentialBranchDraftList = ref<any[]>([]);
const residentialBranchEditState = ref<Record<string, boolean>>({});
const isInitializingBranchResidentialLocation = ref(false);
const suppressBranchLocationCascade = ref(false);
const lastResidentialBranchListKey = ref<string | null>(null);
const isSyncingBranchDraftFromProps = ref(false);

/** MY Corporate branch — L_CONTACT_BRANCH Registered 블록 */
const showBranchResidentialSection = computed(() => useMultiResidentialListUi.value);

const branchResidentialListHasContent = computed(() => {
    if (residentialBranchDraftList.value.length > 0) return true;
    const list = props.residentialAddressList || [];
    if (list.length > 0) return true;
    const pool = props.residentialAddressShowPool || [];
    if (pool.length > 0) return true;
    if (pool.some((row) => isMyCorporateBranchAddressEntityMapped(row))) return true;
    return false;
});

const showBranchResidentialEmptyPlaceholder = computed(
    () =>
        showBranchResidentialSection.value &&
        !branchResidentialListHasContent.value &&
        !props.hideAddressConsolidationUi,
);

function residentialBranchListSyncKey(list: any[]) {
    const cd = String(props.contactCd || '').trim();
    return `${cd}|${(list || []).length}|${JSON.stringify(
        (list || []).map((a) => ({
            s1: a?.street01 || a?.addressLine1,
            s2: a?.street02 || a?.addressLine2,
            pc: a?.post_code || a?.postcode,
            c: a?.country,
            st: a?.state,
            ci: a?.city,
            en: a?.entity_name,
        })),
    )}`;
}

function branchPostcodeOptionsForResolve() {
    return postcodeOptions.value.length > 0
        ? postcodeOptions.value
        : mergedResidentialPostcodeAutocompleteItems.value;
}

function resolveBranchPostcodeTotalCode(address: any) {
    const src = address || {};
    return resolvePostcodeTotalCodeForForm(
        src.post_code ?? src.postcode ?? src.postalCode,
        src.postcodeCode,
        branchPostcodeOptionsForResolve(),
    );
}

function pickBranchPostcodeDisplayName(address: any): string {
    const src = address || {};
    const pool = branchPostcodeOptionsForResolve();
    const fromPrimary = sanitizeMyLocationValueForForm(
        src.post_code ?? src.postcode ?? src.postalCode,
    );
    if (fromPrimary && !isMyLocationGuidLike(fromPrimary)) return fromPrimary;
    const fromCode = sanitizeMyLocationValueForForm(src.postcodeCode);
    if (fromCode && !isMyLocationGuidLike(fromCode)) return fromCode;
    const total = resolvePostcodeTotalCodeForForm(
        src.post_code ?? src.postcode ?? src.postalCode,
        src.postcodeCode,
        pool,
    );
    if (!total) return '';
    return resolvePostcodeDisplayNameFromOptions(total, pool) || '';
}

function ensureBranchPostcodeInOptions(postcodeCode: string, displayName?: string) {
    const code = String(postcodeCode || '').trim();
    if (!code || isMyLocationPlaceholderValue(code)) return;

    const fromCache = mergedResidentialPostcodeAutocompleteItems.value.find(
        (o) => o.TOTAL_CODE === code,
    );
    const label =
        (displayName && !isMyLocationGuidLike(displayName) ? displayName : '') ||
        fromCache?.CODE_NAME ||
        resolvePostcodeDisplayNameFromOptions(code, branchPostcodeOptionsForResolve()) ||
        (!isMyLocationGuidLike(code) ? code : '');

    if (!label) return;

    const normalized = { TOTAL_CODE: code, CODE_NAME: label };
    if (!postcodeOptions.value.some((o) => o.TOTAL_CODE === code)) {
        postcodeOptions.value = upsertOption(postcodeOptions.value, normalized);
    }
}

function pickBranchPostcodeDisplayFromRow(src: Record<string, unknown> | null | undefined): string {
    const raw = sanitizeMyLocationValueForForm(
        src?.post_code ?? src?.postcode ?? src?.postalCode,
    );
    if (raw && !isMyLocationGuidLike(raw)) return raw;
    return '';
}

function isMalaysiaBranchAddressRow(address: any) {
    if (!address) return false;
    ensureMalaysiaCountryWhenMyLocationPresent(address, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
    });
    if (address.country === MY_MALAYSIA_COUNTRY_CODE) {
        address.countryCode = MY_MALAYSIA_COUNTRY_CODE;
        address._countrySearch = countryCodeToSearchLabel(
            address.country,
            countryOptions.value,
        );
    }
    const code = resolveCountryTotalCode(address?.country, countryOptions.value);
    return code === MY_MALAYSIA_COUNTRY_CODE;
}

/** Service/Business mapMyJsonRowForForm과 동일 — state/city처럼 post_code는 표시값(81300) 유지, Edit hydrate에서 TOTAL_CODE 변환 */
function mapBranchResidentialRowForForm(address: any) {
    const src = address || {};
    clearMyLocationPlaceholdersOnAddress(src);
    const postcodeDisplay = pickBranchPostcodeDisplayFromRow(src);
    const prepared: Record<string, unknown> = {
        ...src,
        country:
            sanitizeMyLocationValueForForm(src.country) ||
            sanitizeMyLocationValueForForm(src.countryCode),
        state:
            sanitizeMyLocationValueForForm(src.state) ||
            sanitizeMyLocationValueForForm(src.stateCode),
        city:
            sanitizeMyLocationValueForForm(src.city) ||
            sanitizeMyLocationValueForForm(src.cityCode),
        post_code: postcodeDisplay,
    };
    if (prepared.postcodeCode && isMyLocationGuidLike(prepared.postcodeCode)) {
        delete prepared.postcodeCode;
    }
    const row = normalizeMyLocationRowForForm(prepared, {
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
    });
    if (row.country) {
        row.countryCode = row.country;
    }
    row._countrySearch = countryCodeToSearchLabel(row.country, countryOptions.value);
    row.alias = String(row.alias ?? row.ALIAS ?? '').trim();
    if (postcodeDisplay) {
        row.post_code = postcodeDisplay;
    }
    return row;
}

function remapResidentialBranchDraftInPlace() {
    if (!props.useBranchAddresses || residentialBranchDraftList.value.length === 0) return;
    isSyncingBranchDraftFromProps.value = true;
    try {
        residentialBranchDraftList.value = residentialBranchDraftList.value.map((row) =>
            mapBranchResidentialRowForForm(row),
        );
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
        });
    }
}

/** state/city hydrate + residential bindResidentialPostcodeFromLocal 재사용 (post_code 81300 → TOTAL_CODE) */
async function hydrateBranchResidentialRowFields(
    address: any,
    postcodeDisplayHint = '',
) {
    if (!props.useBranchAddresses || !address) return;
    if (!isMalaysiaBranchAddressRow(address)) return;
    isInitializingBranchResidentialLocation.value = true;
    try {
        await loadAllPostcodeOptions();
        const displayName =
            postcodeDisplayHint ||
            pickBranchPostcodeDisplayFromRow(address) ||
            pickBranchPostcodeDisplayName(address);
        const stateCode =
            resolveTotalCodeFromField(address.state, stateOptions.value) ||
            String(address.state || '').trim();
        if (!stateCode || isMyLocationPlaceholderValue(stateCode)) return;
        const resolvedGuid = resolveTotalCodeFromField(stateCode, stateOptions.value);
        if (resolvedGuid && resolvedGuid !== address.state) {
            address.state = resolvedGuid;
        }
        await loadCitiesByState(stateCode);
        let cityCode = resolveTotalCodeFromField(address.city, cityOptions.value);
        // City 비어 있고 Postcode만 있으면 역검색으로 State/City 채움
        if (!cityCode) {
            const postcodeRaw = String(
                address.post_code || address.postcode || address.postalCode || '',
            ).trim();
            if (postcodeRaw && !isMyLocationPlaceholderValue(postcodeRaw)) {
                if (postcodeAllOptionsCache.value.length === 0) {
                    await loadAllPostcodeOptions();
                }
                const postcodeNumber =
                    mergedResidentialPostcodeAutocompleteItems.value.find(
                        (opt) => opt.TOTAL_CODE === postcodeRaw,
                    )?.CODE_NAME ||
                    pickBranchPostcodeDisplayFromRow(address) ||
                    pickBranchPostcodeDisplayName(address) ||
                    postcodeRaw;
                const locationInfo = await findStateAndCityByPostcode(
                    postcodeNumber,
                    resolvedGuid || stateCode,
                    null,
                );
                if (locationInfo?.stateCode) {
                    address.state = locationInfo.stateCode;
                    await loadCitiesByState(locationInfo.stateCode);
                }
                if (locationInfo?.cityCode) {
                    address.city = locationInfo.cityCode;
                    cityCode = locationInfo.cityCode;
                }
            }
        }
        if (cityCode) {
            await loadPostcodeOptions(cityCode, true);
        }
        const postcodeTotal = await bindResidentialPostcodeFromLocal(
            {
                post_code: displayName || address.post_code,
                postcode: address.postcode,
                postalCode: address.postalCode,
                postcodeCode: address.postcodeCode,
            },
            cityCode,
        );
        if (postcodeTotal) {
            address.post_code = postcodeTotal;
            ensureBranchPostcodeInOptions(postcodeTotal, displayName || undefined);
        }
    } finally {
        setTimeout(() => {
            isInitializingBranchResidentialLocation.value = false;
        }, 100);
    }
}

async function hydrateAllMalaysiaBranchResidentialRows() {
    if (!props.useBranchAddresses || residentialBranchDraftList.value.length === 0) return;
    isSyncingBranchDraftFromProps.value = true;
    try {
        for (const row of residentialBranchDraftList.value) {
            if (!row) continue;
            clearMyLocationPlaceholdersOnAddress(row);
            const countryCode = resolveCountryTotalCode(row.country, countryOptions.value);
            if (countryCode !== MY_MALAYSIA_COUNTRY_CODE) {
                Object.assign(row, mapBranchResidentialRowForForm(row));
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            await hydrateBranchResidentialRowFields(row);
        }
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
        });
    }
}

const getResidentialBranchEditKey = (index: number) => `branch-res-${index}`;

const isResidentialBranchEditing = (index: number) =>
    !!residentialBranchEditState.value[getResidentialBranchEditKey(index)];

const anyResidentialBranchEditing = () =>
    Object.values(residentialBranchEditState.value).some(Boolean);

function syncResidentialBranchDraftFromProps(force = false) {
    if (!useMultiResidentialListUi.value) return;
    if (!force && anyResidentialBranchEditing()) return;
    const src = props.residentialAddressList || [];
    const key = residentialBranchListSyncKey(src);
    if (!force && key === lastResidentialBranchListKey.value) return;

    const mapped = src.map((row) => mapBranchResidentialRowForForm(row));
    const current = residentialBranchDraftList.value;

    // Register: 삭제 후 서버 재로드 전 stale props로 draft가 되살아나지 않도록
    if (
        props.registerAddressDeleteContext
        && !force
        && !anyResidentialBranchEditing()
        && mapped.length > 0
        && current.length <= mapped.length
        && lastResidentialBranchListKey.value === residentialBranchListSyncKey(current)
    ) {
        return;
    }

    lastResidentialBranchListKey.value = key;
    isSyncingBranchDraftFromProps.value = true;
    try {
        const mapped = src.map((row) => mapBranchResidentialRowForForm(row));
        const current = residentialBranchDraftList.value;
        const extraRows =
            !force && current.length > mapped.length
                ? current.slice(mapped.length).map((row) => mapBranchResidentialRowForForm(row))
                : [];
        residentialBranchDraftList.value = [...mapped, ...extraRows];
        if (force) {
            residentialBranchEditState.value = {};
        }
    } finally {
        nextTick(() => {
            isSyncingBranchDraftFromProps.value = false;
            if (countryOptions.value.length > 0 && stateOptions.value.length > 0) {
                void hydrateAllMalaysiaBranchResidentialRows().catch((err) =>
                    console.error('[NoSyncContactAddressForm] hydrate branch rows from props', err),
                );
            }
        });
    }
}

watch(
    () => props.residentialAddressList,
    () => {
        if (!useMultiResidentialListUi.value) return;
        syncResidentialBranchDraftFromProps();
    },
    { immediate: true, deep: true },
);

/** Service/Business serviceRowShowLocationFields와 동일 — draft country 변경에 즉시 반응 */
const branchResidentialRowShowLocationFields = computed(() =>
    residentialBranchDraftList.value.map((row) =>
        shouldShowMyLocationFieldsWithAddDefault(
            row?.country,
            countryOptions.value,
            !!row?._showLocationDefaultsOnAdd,
        ),
    ),
);

const showBranchResidentialShowDialog = ref(false);
const branchResidentialShowEditIndex = ref<number | null>(null);
const branchResidentialShowItems = ref<BranchAddressShowItem[]>([]);

// MY 주소 Consolidate 다이얼로그
const consolidateDialogOpen = ref(false);
const showConsolidateAddressLink = computed(
    () => isJurisdictionMY.value
        && canEditMyAddress.value
        && !!String(props.contactCd || '').trim(),
);
function onAddressConsolidated() {
    emit('refresh');
}

const corporateBranchShowLabels = computed(() => ({
    residential: residentialAddressLabel.value,
    service: t('contact.Service Address') || 'Service Address',
    business: t('contact.Business Address') || 'Business Address',
}));

const entityCdForBranch = computed(() => String(props.entityCd || '').trim());

const residentialRowsForShowAddress = computed(() => {
    const pool = props.residentialAddressShowPool;
    if (entityCdForBranch.value && Array.isArray(pool) && pool.length > 0) {
        return pool;
    }
    return Array.isArray(props.residentialAddressList) ? props.residentialAddressList : [];
});

const openBranchResidentialShowAddress = (index: number) => {
    if (!props.useBranchAddresses || !canEditMyAddress.value || !isResidentialBranchEditing(index)) return;
    branchResidentialShowEditIndex.value = index;
    branchResidentialShowItems.value = buildAllCorporateBranchShowItems({
        residential: residentialRowsForShowAddress.value,
        service: Array.isArray(props.serviceAddressListForShow) ? props.serviceAddressListForShow : [],
        business: Array.isArray(props.businessAddressListForShow) ? props.businessAddressListForShow : [],
        labels: corporateBranchShowLabels.value,
        formatResidential: (row) => formatBranchResidentialDisplay(row),
    });
    showBranchResidentialShowDialog.value = true;
};

const onBranchResidentialShowSelected = async (row: Record<string, unknown>) => {
    const index = branchResidentialShowEditIndex.value;
    if (index == null || index < 0) return;
    const key = getResidentialBranchEditKey(index);
    if (!residentialBranchEditState.value[key]) {
        residentialBranchEditState.value = {
            ...residentialBranchEditState.value,
            [key]: true,
        };
    }
    await loadCountryOptions();
    await loadStateOptions();
    const draft = residentialBranchDraftList.value[index];
    if (!draft) return;
    const applyOpts = entityCdForBranch.value
        ? { repointEntityMapBranch: true, entityCd: entityCdForBranch.value }
        : { preserveDraftBranchIdentity: true };
    applyBranchAddressRowToDraft(
        draft,
        row,
        {
            countryOpts: countryOptions.value,
            stateOpts: stateOptions.value,
            cityOpts: cityOptions.value,
        },
        applyOpts,
    );
    draft._countrySearch = countryCodeToSearchLabel(draft.country, countryOptions.value);
    Object.assign(draft, mapBranchResidentialRowForForm(draft));
    await hydrateBranchResidentialRowFields(draft);
};

function createEmptyBranchResidentialRow() {
    return {
        alias: '',
        street01: '',
        street02: '',
        street03: '',
        country: MY_MALAYSIA_COUNTRY_CODE,
        state: '',
        city: '',
        post_code: '',
        _countrySearch: '',
        _showLocationDefaultsOnAdd: true,
        _isNewDraftRow: true,
    };
}

const addBranchResidentialRow = async () => {
    if (!canEditMyAddress.value || !useMultiResidentialListUi.value) return;
    await loadBranchMyDisplayOptions();
    await loadCountryOptions();
    await loadStateOptions();
    await loadAllPostcodeOptions();
    const mapped = mapBranchResidentialRowForForm(createEmptyBranchResidentialRow());
    const newIndex = residentialBranchDraftList.value.length;
    residentialBranchDraftList.value = [...residentialBranchDraftList.value, mapped];
    lastResidentialBranchListKey.value = residentialBranchListSyncKey(residentialBranchDraftList.value);
    residentialBranchEditState.value = {
        ...residentialBranchEditState.value,
        [getResidentialBranchEditKey(newIndex)]: true,
    };
    if (useContactListAddressSectionCollapse.value) {
        addressSectionExpanded.value = true;
    }
};

const canDeleteBranchResidentialRow = (_address?: any) => canEditMyAddress.value;

const removeBranchResidentialRow = async (index: number) => {
    if (!canEditMyAddress.value || !useMultiResidentialListUi.value) return;
    const addr = residentialBranchDraftList.value[index];
    if (!canDeleteBranchResidentialRow(addr)) return;

    const newList = residentialBranchDraftList.value.filter((_, i) => i !== index);
    const lists = props.resolveBranchListsForPersist
        ? props.resolveBranchListsForPersist(newList)
        : {
              residentialAddressList: newList.map((r) => ({ ...r })),
              serviceAddressList: [],
              businessAddressList: [],
          };

    const persistOnDelete = shouldPersistMyAddressDelete({
        registerAddressDeleteContext: props.registerAddressDeleteContext,
        contactCd: props.contactCd,
        useBranchAddresses: props.useBranchAddresses,
        contactListFlatAddressMode: props.contactListFlatAddressMode,
        row: addr,
    });
    const ok = await confirmAndDeleteBranchAddress(
        `residential-${index}`,
        lists,
        persistOnDelete,
    );
    if (!ok) return;

    residentialBranchDraftList.value = newList;
    lastResidentialBranchListKey.value = residentialBranchListSyncKey(newList);
    const nextEdit: Record<string, boolean> = {};
    Object.entries(residentialBranchEditState.value).forEach(([k, v]) => {
        if (!k.startsWith('branch-res-')) nextEdit[k] = v;
    });
    residentialBranchEditState.value = nextEdit;
    emitResidentialBranchDraft();
};

const toggleResidentialBranchEdit = async (index: number) => {
    if (!canEditMyAddress.value) return;
    const key = getResidentialBranchEditKey(index);
    const next = !residentialBranchEditState.value[key];
    residentialBranchEditState.value = { ...residentialBranchEditState.value, [key]: next };
    if (next) {
        const row = residentialBranchDraftList.value[index];
        if (row?._isNewDraftRow) {
            await hydrateBranchResidentialRowFields(row);
            return;
        }
        const srcRow = (props.residentialAddressList || [])[index] as Record<string, unknown> | undefined;
        const postcodeHint = pickBranchPostcodeDisplayFromRow(srcRow);
        if (row) {
            Object.assign(row, mapBranchResidentialRowForForm(srcRow || row));
            await hydrateBranchResidentialRowFields(row, postcodeHint);
        }
    } else {
        const src = props.residentialAddressList || [];
        if (src[index]) {
            isSyncingBranchDraftFromProps.value = true;
            try {
                residentialBranchDraftList.value[index] = mapBranchResidentialRowForForm(src[index]);
            } finally {
                nextTick(() => {
                    isSyncingBranchDraftFromProps.value = false;
                });
            }
        } else {
            residentialBranchDraftList.value = residentialBranchDraftList.value.filter(
                (_, i) => i !== index,
            );
        }
        if (!anyResidentialBranchEditing()) return;
        emitResidentialBranchDraft();
    }
};

/** Service onContactJsonAddressCountryUpdate와 동일 — v-model이 country 설정, hydrate로 country 되돌리지 않음 */
function onBranchResidentialCountryUpdate(address: any, newCountry: string | null | undefined) {
    if (!address || typeof address !== 'object') return;
    const c = newCountry != null ? String(newCountry) : '';
    const prevCountry =
        address._countryChangeBaseline !== undefined
            ? String(address._countryChangeBaseline)
            : resolveCountryTotalCode(address.country, countryOptions.value);
    if (c) {
        delete address._showLocationDefaultsOnAdd;
    }
    if (
        applyCountryChangeClearsSubLocation(
            address,
            prevCountry,
            c,
            countryOptions.value,
        )
    ) {
        postcodeOptions.value = [];
        cityOptions.value = [];
    }
    const resolved = resolveCountryTotalCode(c, countryOptions.value);
    address.countryCode = resolved || '';
    address._countryChangeBaseline = resolved;
}

function syncBranchResidentialCountrySearch(address: any) {
    if (!address || typeof address !== 'object') return;
    address._countrySearch = countryCodeToSearchLabel(address.country, countryOptions.value);
}

function onBranchResidentialCountryFocus(address: any) {
    if (address && typeof address === 'object') {
        address._countryChangeBaseline = resolveCountryTotalCode(
            address.country,
            countryOptions.value,
        );
    }
    syncBranchResidentialCountrySearch(address);
}

function onBranchResidentialCountrySelect(address: any, newCountry: string | null | undefined) {
    onBranchResidentialCountryUpdate(address, newCountry);
    if (!address || typeof address !== 'object') return;
    address._countrySearch = newCountry
        ? countryCodeToSearchLabel(newCountry, countryOptions.value)
        : '';
}

async function onBranchResidentialStateSelected(
    address: any,
    newState: string | null | undefined,
) {
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    if (suppressBranchLocationCascade.value) return;

    const st = newState != null ? String(newState) : '';
    if (st && address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }
    address.city = '';
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    if (st) {
        const stateApi = resolveTotalCodeFromField(st, stateOptions.value) || st;
        await loadCitiesByState(stateApi);
    } else {
        cityOptions.value = [];
        cityOptionsForStateCode.value = '';
        if (postcodeAllOptionsCache.value.length === 0) {
            await loadAllPostcodeOptions();
        }
    }
}

async function onBranchResidentialCitySelected(
    address: any,
    newCity: string | null | undefined,
) {
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    if (suppressBranchLocationCascade.value) return;

    const ct = newCity != null ? String(newCity) : '';
    if (ct && address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }
    address.post_code = '';
    address.postcode = '';
    address.postcodeCode = '';
    if (ct) {
        const cityApi = resolveTotalCodeFromField(ct, cityOptions.value) || ct;
        await loadPostcodeOptions(cityApi, false);
    } else {
        postcodeOptions.value = [];
        postcodeOptionsForCityCode.value = '';
        if (postcodeAllOptionsCache.value.length === 0) {
            await loadAllPostcodeOptions();
        }
    }
}

function resolveBranchPostcodeNumber(postcodeValue: string): string {
    const raw = String(postcodeValue || '').trim();
    if (!raw) return '';
    const hit = mergedResidentialPostcodeAutocompleteItems.value.find(
        (opt) => opt.TOTAL_CODE === raw || opt.CODE_NAME === raw,
    );
    if (hit?.CODE_NAME) return hit.CODE_NAME;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
        return '';
    }
    return raw;
}

async function handleBranchResidentialPostcodeChange(address: any) {
    if (!address) return;
    if (!props.useBranchAddresses && !props.contactListFlatAddressMode) return;
    const postcodeValue = String(address.post_code || address.postcode || '').trim();
    if (!postcodeValue) return;

    if (address.country !== MY_MALAYSIA_COUNTRY_CODE) {
        address.country = MY_MALAYSIA_COUNTRY_CODE;
    }

    if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
    }

    const postcodeNumber = resolveBranchPostcodeNumber(postcodeValue);
    if (!postcodeNumber) return;

    const currentStateCode =
        resolveTotalCodeFromField(address.state, stateOptions.value) ||
        (address.state as string) ||
        null;
    const currentCityCode =
        resolveTotalCodeFromField(address.city, cityOptions.value) ||
        (address.city as string) ||
        null;

    const locationInfo = await findStateAndCityByPostcode(
        postcodeNumber,
        currentStateCode,
        currentCityCode,
    );

    if (!locationInfo?.stateCode || !locationInfo.cityCode) return;

    suppressBranchLocationCascade.value = true;
    address._suppressLocationCascade = true;
    isInitializingBranchResidentialLocation.value = true;
    try {
        address.state = locationInfo.stateCode;
        await loadCitiesByState(locationInfo.stateCode);
        address.city = locationInfo.cityCode;
        await loadPostcodeOptions(locationInfo.cityCode, true);
        const keepPc =
            mergedResidentialPostcodeAutocompleteItems.value.find(
                (opt) => opt.TOTAL_CODE === postcodeValue || opt.CODE_NAME === postcodeNumber,
            )?.TOTAL_CODE || postcodeValue;
        address.post_code = keepPc;
        address.postcodeCode = keepPc;
    } finally {
        await nextTick();
        address._suppressLocationCascade = false;
        suppressBranchLocationCascade.value = false;
        isInitializingBranchResidentialLocation.value = false;
    }
}

function queueBranchResidentialState(address: any, v: string | null | undefined) {
    void onBranchResidentialStateSelected(address, v).catch((err) =>
        console.error('[NoSyncContactAddressForm] branch residential state selection', err),
    );
}

function queueBranchResidentialCity(address: any, v: string | null | undefined) {
    void onBranchResidentialCitySelected(address, v).catch((err) =>
        console.error('[NoSyncContactAddressForm] branch residential city selection', err),
    );
}

function queueBranchResidentialPostcode(address: any) {
    void handleBranchResidentialPostcodeChange(address).catch((err) =>
        console.error('[NoSyncContactAddressForm] branch residential postcode selection', err),
    );
}

function sanitizeBranchResidentialRowForEmit(row: any) {
    const copy = { ...(row || {}) };
    clearMyLocationPlaceholdersOnAddress(copy);
    copy.country = normalizeMyLocationFieldForPersist(copy.country, countryOptions.value);
    if (copy.country) {
        copy.countryCode = copy.country;
    }
    if (isMalaysiaBranchAddressRow(copy)) {
        copy.state = normalizeMyLocationFieldForPersist(copy.state, stateOptions.value);
        copy.city = normalizeMyLocationFieldForPersist(copy.city, cityOptions.value);
        if (copy.state) copy.stateCode = copy.state;
        if (copy.city) copy.cityCode = copy.city;
        const pcTotal = resolveBranchPostcodeTotalCode(copy);
        if (pcTotal) {
            copy.post_code = pcTotal;
            copy.postcodeCode = pcTotal;
        }
    }
    return copy;
}

function emitResidentialBranchDraft() {
    const list = residentialBranchDraftList.value.map((r) => sanitizeBranchResidentialRowForEmit(r));
    emit('update:residentialAddressList', list);
    if (props.useBranchAddresses || props.contactListFlatAddressMode) return;
    const primary = list[0];
    if (!primary || !addressData.value) return;
    const localAddress = buildMyLocalAddressRecordFromForm({
        addressLine1: primary.street01 || primary.addressLine1 || '',
        addressLine2: primary.street02 || primary.addressLine2 || '',
        addressLine3: primary.street03 || primary.addressLine3 || '',
        country: primary.country || primary.countryCode || '',
        state: primary.state || primary.stateCode || '',
        city: primary.city || primary.cityCode || '',
        postcode:
            primary.postcodeCode ||
            primary.post_code ||
            primary.postcode ||
            primary.postalCode ||
            '',
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
    });
    addressData.value = { ...addressData.value, localAddress: localAddress as any };
    myAddressData.value = {
        addressLine1: localAddress.addressLine1,
        addressLine2: localAddress.addressLine2,
        addressLine3: localAddress.addressLine3,
        country: localAddress.country,
        state: localAddress.state,
        city: localAddress.city,
        postcode: localAddress.postcode,
    };
    emit('update:modelValue', addressData.value);
}

/** Corporate branch: 편집 중 부모 emit 금지(ContactInfoForm Service/Business와 동일) — Save 시 expose */
watch(
    residentialBranchDraftList,
    () => {
        if (!showBranchResidentialSection.value) return;
        if (isSyncingBranchDraftFromProps.value) return;
        if (props.useBranchAddresses || props.contactListFlatAddressMode) return;
        if (!anyResidentialBranchEditing()) return;
        emitResidentialBranchDraft();
    },
    { deep: true },
);

function buildResidentialBranchListForSave() {
    return residentialBranchDraftList.value.map((r) => sanitizeBranchResidentialRowForEmit(r));
}

defineExpose({
    getResidentialBranchListForSave: () =>
        useMultiResidentialListUi.value ? buildResidentialBranchListForSave() : null,
    /** Save 직전 — myAddressData → localAddress flush (state/city 누락 방지) */
    getLocalAddressForSave: () => buildMyLocalAddressFromMyAddressData(),
});

watch(
    () => props.contactCd,
    () => {
        lastResidentialBranchListKey.value = null;
        syncResidentialBranchDraftFromProps(true);
        if (useMultiResidentialListUi.value) {
            void loadBranchMyDisplayOptions();
        }
    },
);

watch(
    () => countryOptions.value.length,
    (len, prevLen) => {
        if (!props.useBranchAddresses || len === 0) return;
        if (prevLen === len && len > 0) return;
        remapResidentialBranchDraftInPlace();
    },
);

watch(
    () => stateOptions.value.length,
    (len, prevLen) => {
        if (!props.useBranchAddresses || len === 0) return;
        if (prevLen === len && len > 0) return;
        remapResidentialBranchDraftInPlace();
        void hydrateAllMalaysiaBranchResidentialRows().catch((err) =>
            console.error('[NoSyncContactAddressForm] hydrate after state options', err),
        );
    },
);

/** MY Corporate branch — 표시용 country/state만 */
async function loadBranchMyDisplayOptions() {
    if (!props.useBranchAddresses) return;
    await loadCountryOptions();
    await loadStateOptions();
    if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
    }
    if (Array.isArray(props.residentialAddressList) && props.residentialAddressList.length > 0) {
        if (!anyResidentialBranchEditing()) {
            syncResidentialBranchDraftFromProps(true);
        }
    } else {
        await hydrateAllMalaysiaBranchResidentialRows();
    }
    await hydrateEditingBranchResidentialRows();
}

async function hydrateEditingBranchResidentialRows() {
    if (!props.useBranchAddresses) return;
    const tasks: Promise<void>[] = [];
    Object.entries(residentialBranchEditState.value).forEach(([key, editing]) => {
        if (!editing) return;
        const m = key.match(/^branch-res-(\d+)$/);
        if (!m) return;
        const row = residentialBranchDraftList.value[Number(m[1])];
        if (row) {
            tasks.push(
                hydrateBranchResidentialRowFields(row).catch((err) =>
                    console.error('[NoSyncContactAddressForm] hydrate branch residential', err),
                ),
            );
        }
    });
    if (tasks.length > 0) await Promise.all(tasks);
}

onErrorCaptured((err, _instance, info) => {
    console.error('[NoSyncContactAddressForm] error', err, info);
    return false;
});

onMounted(async () => {
    window.addEventListener('language-changed', (event: any) => {
        currentLanguage.value = event.detail.language;
    });

    console.log('[NoSyncContactAddressForm] mounted', {
        contactCd: props.contactCd,
        useBranchAddresses: props.useBranchAddresses,
        residentialListCount: Array.isArray(props.residentialAddressList)
            ? props.residentialAddressList.length
            : 0,
        draftCount: residentialBranchDraftList.value.length,
        showBranchResidentialSection: showBranchResidentialSection.value,
    });

    try {
        if (isJurisdictionMY.value) {
            await loadMyOptions();
            if (props.useBranchAddresses) {
                await loadBranchMyDisplayOptions();
            }
        } else if (props.useBranchAddresses) {
            await loadBranchMyDisplayOptions();
        }
    } catch (mountErr) {
        console.error('[NoSyncContactAddressForm] onMounted failed', mountErr);
    }
});

watch(isJurisdictionMY, async (newValue, oldValue) => {
    if (newValue && !oldValue) {
        await loadMyOptions();
        if (props.useBranchAddresses) {
            await loadBranchMyDisplayOptions();
        }
    }
}, { immediate: false });

watch(
    () => props.useBranchAddresses,
    async (useBranch) => {
        if (!useBranch) return;
        await loadBranchMyDisplayOptions();
    },
);

const shouldShowLocationFields = computed(() =>
    shouldShowMyLocationFieldsWithAddDefault(
        myAddressData.value.country,
        countryOptions.value,
        residentialShowLocationOnAdd.value
    )
);

const countrySearch = ref('');

function syncResidentialCountrySearchFromModel() {
    countrySearch.value = countryCodeToSearchLabel(
        myAddressData.value.country,
        countryOptions.value
    );
}

function syncResidentialCountrySnapshotFromMyData() {
    residentialCountrySnapshot.value = resolveCountryTotalCode(
        myAddressData.value.country,
        countryOptions.value
    );
}

function onResidentialCountryFocus() {
    syncResidentialCountrySearchFromModel();
    residentialCountrySnapshot.value = resolveCountryTotalCode(
        myAddressData.value.country,
        countryOptions.value
    );
}

function onMyResidentialCountryUpdate(newCountry: string | null | undefined) {
    const c = newCountry != null ? String(newCountry) : '';
    if (c) residentialShowLocationOnAdd.value = false;
    const prevCountry = residentialCountrySnapshot.value;
    if (
        applyCountryChangeClearsSubLocation(
            myAddressData.value as Record<string, unknown>,
            prevCountry,
            c,
            countryOptions.value
        )
    ) {
        isResettingLocationFromCountryChange.value = true;
        postcodeOptions.value = [];
        cityOptions.value = [];
        nextTick(() => {
            isResettingLocationFromCountryChange.value = false;
        });
    }
    residentialCountrySnapshot.value = resolveCountryTotalCode(c, countryOptions.value);
    countrySearch.value = c
        ? countryCodeToSearchLabel(c, countryOptions.value)
        : '';
}

function buildMyLocalAddressFromMyAddressData(): Record<string, unknown> {
    const nv = myAddressData.value;
    const currentLocalAddress = (props.modelValue as any)?.localAddress || {};
    const extra: Record<string, unknown> = {};
    if (currentLocalAddress.IsSync !== undefined) extra.IsSync = currentLocalAddress.IsSync;
    if (currentLocalAddress.isSync !== undefined) extra.isSync = currentLocalAddress.isSync;

    return buildMyLocalAddressRecordFromForm({
        addressLine1: nv.addressLine1,
        addressLine2: nv.addressLine2,
        addressLine3: nv.addressLine3,
        country: nv.country,
        state: nv.state,
        city: nv.city,
        postcode: nv.postcode,
        countryOpts: countryOptions.value,
        stateOpts: stateOptions.value,
        cityOpts: cityOptions.value,
        postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
        extra,
    });
}

function myAddressDataMatchesLocalAddress(local: any): boolean {
    if (!local) return false;
    const nv = local as Record<string, unknown>;
    const countryCode =
        resolveTotalCodeFromField(nv.countryCode || local.country, countryOptions.value) ||
        String(local.country || '').trim();
    const stateCode =
        resolveTotalCodeFromField(nv.stateCode || local.state, stateOptions.value) ||
        String(local.state || '').trim();
    const cityCode =
        resolveTotalCodeFromField(nv.cityCode || local.city, cityOptions.value) ||
        String(local.city || '').trim();
    const postcodeCode =
        resolveTotalCodeFromField(
            (local as any).post_code ||
                local.postalCode ||
                (local as any).postcodeCode ||
                local.postcode,
            postcodeOptions.value,
        ) || String(local.postcode || '').trim();
    const md = myAddressData.value;
    return (
        (md.addressLine1 || '') === (local.addressLine1 || '') &&
        (md.addressLine2 || '') === (local.addressLine2 || '') &&
        (md.addressLine3 || '') === (local.addressLine3 || '') &&
        (md.country || '') === (countryCode || '') &&
        (md.state || '') === (stateCode || '') &&
        (md.city || '') === (cityCode || '') &&
        (md.postcode || '') === (postcodeCode || '')
    );
}

// 필드별 readonly 계산
const isFieldReadonly = (fieldPath: string) => {
    return props.readonly;
};

// MY 한 줄 요약·읽기 전용 필드: Service(alt)와 같이 TOTAL_CODE → CODE_NAME 옵션 조회 후, 없으면 JSON 텍스트
const getCountryDisplayName = computed(() => {
    const code = String(myAddressData.value.country || '').trim();
    if (code) {
        const option = countryOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return String(option.CODE_NAME || '').toUpperCase();
    }
    const originalValue = String(props.modelValue?.localAddress?.country || '').trim();
    if (originalValue) return originalValue.toUpperCase();
    return code.toUpperCase();
});

const getStateDisplayName = computed(() => {
    const code = String(myAddressData.value.state || '').trim();
    if (code) {
        const option = stateOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return option.CODE_NAME;
    }
    const originalValue = String(props.modelValue?.localAddress?.state || '').trim();
    if (originalValue) return originalValue;
    return code;
});

const getCityDisplayName = computed(() => {
    const code = String(myAddressData.value.city || '').trim();
    if (code) {
        const option = cityOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return option.CODE_NAME;
    }
    const originalValue = String(props.modelValue?.localAddress?.city || '').trim();
    if (originalValue) return originalValue;
    return code;
});

const getPostcodeDisplayName = computed(() => {
    const code = String(myAddressData.value.postcode || '').trim();
    if (code) {
        const option = postcodeOptions.value.find((opt) => opt.TOTAL_CODE === code);
        if (option) return option.CODE_NAME;
    }
    const modelLocalAddr = props.modelValue?.localAddress;
    const originalValue = String(
        (modelLocalAddr as any)?.post_code ||
            modelLocalAddr?.postcode ||
            modelLocalAddr?.postalCode ||
            ''
    ).trim();
    if (originalValue) return originalValue;
    return code;
});

const mergedMyAddress = computed(() =>
    formatMyResidentialSummaryForDisplay(
        myAddressData.value,
        props.modelValue?.localAddress as Record<string, unknown> | undefined,
        {
            countryOpts: countryOptions.value,
            stateOpts: stateOptions.value,
            cityOpts: cityOptions.value,
            postcodeOpts: mergedResidentialPostcodeAutocompleteItems.value,
        }
    )
);

/** 풀싱크 등으로 주소에 붙는 IsSync / isSync */
const isResidentialAddressSync = computed(() => {
    const la = props.modelValue?.localAddress as any;
    if (!la) return false;
    const v = la.IsSync ?? la.isSync;
    return v === 1 || v === '1' || v === true;
});

const hasMergedMyAddress = computed(() => mergedMyAddress.value.length > 0);
const showMyAddressReadonlyEmpty = computed(
    () => isJurisdictionMY.value && readonly.value && !hasMergedMyAddress.value
);
const showResidentialAddPlaceholder = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        !readonly.value &&
        !hasMergedMyAddress.value &&
        !residentialMyExpanded.value
);
const showMyAddressSummary = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        hasMergedMyAddress.value &&
        !isMyAddressEditing.value
);
const showMyAddressEditor = computed(
    () =>
        isJurisdictionMY.value &&
        !showBranchResidentialSection.value &&
        !showMyAddressSummary.value &&
        !showResidentialAddPlaceholder.value &&
        !showMyAddressReadonlyEmpty.value
);

const showAddressSelectDialog = ref(false);
const contactCdForSelect = computed(() => String(props.contactCd || '').trim());

async function onAddressCandidateSelected(candidate: ContactAddressCandidate) {
    if (candidate.addressType !== 'M04001') return;
    isInitializingLocation.value = true;
    try {
        await loadCountryOptions();
        await loadStateOptions();
        const mapped = mapCandidateToMyAddressData(candidate.address);
        let countryCode =
            resolveTotalCodeFromField(mapped.country, countryOptions.value) || mapped.country;
        let stateCode =
            resolveTotalCodeFromField(mapped.state, stateOptions.value) || mapped.state;
        if (stateCode) {
            await loadCitiesByState(stateCode);
        }
        let cityCode =
            resolveTotalCodeFromField(mapped.city, cityOptions.value) || mapped.city;
        if (cityCode) {
            await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
        }
        const postcodeCode =
            resolveTotalCodeFromField(mapped.postcode, postcodeOptions.value) || mapped.postcode;
        myAddressData.value = {
            addressLine1: mapped.addressLine1,
            addressLine2: mapped.addressLine2,
            addressLine3: mapped.addressLine3,
            country: countryCode,
            state: stateCode,
            city: cityCode,
            postcode: postcodeCode,
        };
    } finally {
        await nextTick();
        isInitializingLocation.value = false;
    }
}

const toggleMyAddressEdit = async () => {
  if (!canEditMyAddress.value) return;
  const next = !isMyAddressEditing.value;
  if (next) {
    await hydrateResidentialMyAddressFields();
  }
  isMyAddressEditing.value = next;
};

const openResidentialMyAdd = async () => {
    if (!canEditMyAddress.value) return;
    residentialMyExpanded.value = true;
    residentialShowLocationOnAdd.value = true;
    isMyAddressEditing.value = true;
    await loadMyOptions();
    void loadAllPostcodeOptions().catch(() => {});
};

const cancelResidentialMyDraft = async () => {
    residentialMyExpanded.value = false;
    residentialShowLocationOnAdd.value = false;
    isMyAddressEditing.value = false;
    myAddressData.value = {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        country: '',
        state: '',
        city: '',
        postcode: ''
    };
    await loadMyOptions();
};

watch(() => isJurisdictionMY.value, (newValue) => {
  if (!newValue) {
    isMyAddressEditing.value = false;
    residentialMyExpanded.value = false;
  }
});

watch(() => readonly.value, (newValue) => {
  if (newValue) {
    isMyAddressEditing.value = false;
    residentialMyExpanded.value = false;
  }
});

watch(hasMergedMyAddress, (hasAddr) => {
    if (hasAddr) {
        residentialMyExpanded.value = false;
    }
});

// MY 비읽기·주소 없음: placeholder(-)만 표시. + Add 로 펼친 뒤 필드 표시.

const addressData: WritableComputedRef<AddressModel> = computed<AddressModel>({
    get: () => {
        const value = props.modelValue || { addressType: 'B07001' };
        const normalizedAddressType: AddressType = value.addressType === 'B07002' ? 'B07002' : 'B07001';

        const localAddr = value.localAddress || {
            postalCode: '',
            blockHouseNo: '',
            streetName: '',
            buildingEstateName: '',
            level: '',
            unit: '',
            // MY인 경우 Entity 스타일 주소 필드
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            country: '',
            state: '',
            city: '',
            postcode: '',
        };

        // post_code가 있으면 포함
        if (value.localAddress && (value.localAddress as any).post_code) {
            (localAddr as any).post_code = (value.localAddress as any).post_code;
        }

        return {
            addressType: normalizedAddressType,
            localAddress: localAddr,
            foreignAddress: value.foreignAddress || {
                addressLine1: '',
                addressLine2: '',
            },
        };
    },
    set: (value: AddressModel) => {
        const normalizedAddressType: AddressType = value.addressType === 'B07002' ? 'B07002' : 'B07001';

        emit('update:modelValue', {
            ...value,
            addressType: normalizedAddressType
        });
    }
});

// Postcode 옵션 로드 (City 선택 시) — myAddressData 선언 이후
const loadPostcodeOptions = async (cityCode: string | null, preservePostcodeValue: boolean = false) => {
  if (!cityCode) {
    postcodeOptionsForCityCode.value = '';
    if (postcodeAllOptionsCache.value.length === 0) {
      await loadAllPostcodeOptions();
    }
    if (!preservePostcodeValue) {
      myAddressData.value.postcode = '';
    }
    return;
  }

  const resolvedCity =
    resolveTotalCodeFromField(cityCode, cityOptions.value) || String(cityCode).trim();
  if (!resolvedCity || isMyLocationPlaceholderValue(resolvedCity)) {
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    return;
  }

  isLoadingPostcodes.value = true;
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Postcode 목록 로드 타임아웃')), 5000)
    );
    
    const response = await Promise.race([
      entityMyApi.getMalaysiaPostcodes(resolvedCity),
      timeoutPromise
    ]) as any;
    
    const postcodes = response.data?.data || response.data || [];
    
    // CODE_NAME(우편번호 숫자) 기준으로 중복 제거
    const uniquePostcodesMap = new Map<string, { TOTAL_CODE: string; CODE_NAME: string }>();
    
    postcodes.forEach((postcode: any) => {
      const codeName = String(postcode.name || '').trim();
      const totalCode = String(postcode.code || postcode.name || '').trim();
      
      if (codeName && totalCode) {
        if (!uniquePostcodesMap.has(codeName)) {
          uniquePostcodesMap.set(codeName, {
            TOTAL_CODE: totalCode,
            CODE_NAME: codeName
          });
        }
      }
    });
    
    postcodeOptions.value = Array.from(uniquePostcodesMap.values());
    postcodeOptionsForCityCode.value = resolvedCity.toLowerCase();
  } catch (error) {
    console.error('❌ [loadPostcodeOptions] Postcode 로드 실패:', error);
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
  } finally {
    isLoadingPostcodes.value = false;
  }
};

async function ensurePostcodesLoadedForCity(cityRaw: unknown) {
  const city = String(cityRaw ?? '').trim();
  if (!city || isMyLocationPlaceholderValue(city)) return;
  const resolved = resolveTotalCodeFromField(city, cityOptions.value) || city;
  const resolvedKey = resolved.toLowerCase();
  if (
    postcodeOptions.value.length > 0 &&
    postcodeOptionsForCityCode.value &&
    postcodeOptionsForCityCode.value === resolvedKey
  ) {
    return;
  }
  await loadPostcodeOptions(city, true);
}

async function onBranchResidentialPostcodeMenu(address: any, open: boolean) {
  if (!open) return;
  await ensureResidentialPostcodeFullCacheForReverse();
  if (address?.city) {
    await ensurePostcodesLoadedForCity(address.city);
  }
}

/** MyAddressSection / state·city와 동일 — post_code → TOTAL_CODE, postcodeOptions upsert */
async function bindResidentialPostcodeFromLocal(
    localAddr: Record<string, unknown>,
    cityCode: string,
): Promise<string> {
    let postcodeCode = String((localAddr as any).postcodeCode ?? '').trim();
    const postcodeNameRaw = String(
        (localAddr as any).post_code ?? localAddr.postalCode ?? localAddr.postcode ?? '',
    ).trim();
    const postcodeName = isMyLocationGuidLike(postcodeNameRaw) ? '' : postcodeNameRaw;

    if (
        postcodeCode &&
        isMyLocationGuidLike(postcodeCode) &&
        !postcodeOptions.value.some((o) => o.TOTAL_CODE === postcodeCode)
    ) {
        postcodeCode = '';
    }

    if (postcodeName && !postcodeCode && cityCode) {
        if (postcodeOptions.value.length === 0) {
            await loadPostcodeOptions(cityCode, true).catch(() => {});
        }
        const postcodeMatch = postcodeOptions.value.find(
            (opt) =>
                opt.CODE_NAME === postcodeName ||
                opt.CODE_NAME?.toUpperCase() === postcodeName.toUpperCase(),
        );
        if (postcodeMatch) {
            postcodeCode = postcodeMatch.TOTAL_CODE;
        }
    }

    if (!postcodeCode) {
        postcodeCode = resolveTotalCodeFromField(
            (localAddr as any).post_code ||
                localAddr.postalCode ||
                localAddr.postcode ||
                (localAddr as any).postcodeCode,
            postcodeOptions.value,
        );
    }

    if (
        postcodeCode &&
        isMyLocationGuidLike(postcodeCode) &&
        !postcodeOptions.value.some((o) => o.TOTAL_CODE === postcodeCode) &&
        postcodeName &&
        !isMyLocationGuidLike(postcodeName)
    ) {
        const byName = postcodeOptions.value.find(
            (opt) =>
                opt.CODE_NAME === postcodeName ||
                opt.CODE_NAME?.toUpperCase() === postcodeName.toUpperCase(),
        );
        postcodeCode = byName?.TOTAL_CODE || '';
    }

    if (postcodeCode && postcodeCode.trim()) {
        let postcodeExists = postcodeOptions.value.some((opt) => opt.TOTAL_CODE === postcodeCode);
        if (!postcodeExists && cityCode) {
            await loadPostcodeOptions(cityCode, true).catch(() => {});
            postcodeExists = postcodeOptions.value.some((opt) => opt.TOTAL_CODE === postcodeCode);
        }
        if (!postcodeExists) {
            let postcodeDisplayName = postcodeName && !isMyLocationGuidLike(postcodeName) ? postcodeName : '';
            if (!postcodeDisplayName) {
                if (postcodeAllOptionsCache.value.length === 0) {
                    await loadAllPostcodeOptions();
                }
                const postcodeMatch =
                    postcodeOptions.value.find((opt) => opt.TOTAL_CODE === postcodeCode) ||
                    postcodeAllOptionsCache.value.find((opt) => opt.TOTAL_CODE === postcodeCode);
                postcodeDisplayName = postcodeMatch?.CODE_NAME || '';
            }
            if (postcodeDisplayName) {
                postcodeOptions.value = upsertOption(postcodeOptions.value, {
                    TOTAL_CODE: String(postcodeCode).trim(),
                    CODE_NAME: postcodeDisplayName,
                });
            }
        }
    }

    return postcodeCode;
}

async function bindResidentialMyAddressDataFromLocal(localAddr: Record<string, unknown>) {
    await loadCountryOptions();
    await loadStateOptions();
    await loadAllPostcodeOptions();

    const countryCode = resolveTotalCodeFromField(
        (localAddr as any).countryCode || localAddr.country,
        countryOptions.value,
    );
    const stateCode = resolveTotalCodeFromField(
        (localAddr as any).stateCode || localAddr.state,
        stateOptions.value,
    );
    if (stateCode) {
        await loadCitiesByState(stateCode);
    }
    const cityCode = resolveTotalCodeFromField(
        (localAddr as any).cityCode || localAddr.city,
        cityOptions.value,
    );
    if (cityCode) {
        await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
    }
    const postcodeCode = await bindResidentialPostcodeFromLocal(localAddr, cityCode);

    myAddressData.value = {
        addressLine1: String(localAddr.addressLine1 || (localAddr as any).street01 || ''),
        addressLine2: String(localAddr.addressLine2 || (localAddr as any).street02 || ''),
        addressLine3: String(localAddr.addressLine3 || (localAddr as any).street03 || ''),
        country: countryCode,
        state: stateCode,
        city: cityCode,
        postcode: postcodeCode,
    };
}

// 우편번호로 State와 City 찾기 (이미 선택된 State/City 정보 활용)
const findStateAndCityByPostcode = async (
  postcodeNumber: string,
  currentStateCode?: string | null,
  currentCityCode?: string | null
) => {
  if (!postcodeNumber || postcodeNumber.trim().length === 0) {
    return null;
  }
  
  try {
    const response = await entityMyApi.getMalaysiaPostcodesByNumber(postcodeNumber.trim());
    const postcodes = (response as any).data?.data || (response as any).data || [];
    
    if (!Array.isArray(postcodes) || postcodes.length === 0) {
      return null;
    }
    
    // 이미 선택된 City가 있으면 해당 City와 일치하는 결과 우선 선택
    if (currentCityCode && postcodes.length > 1) {
      const cityKey = String(currentCityCode).toLowerCase();
      const matchedByCity = postcodes.find(
        (p: any) => String(p.city?.code || '').toLowerCase() === cityKey,
      );
      if (matchedByCity) {
        return {
          stateCode: matchedByCity?.state?.code || null,
          cityCode: matchedByCity?.city?.code || null,
          cityName: matchedByCity?.city?.name || null
        };
      }
    }
    
    // City가 없고 State만 선택되어 있으면 해당 State의 결과 우선 선택
    if (currentStateCode && !currentCityCode && postcodes.length > 1) {
      const stateKey = String(currentStateCode).toLowerCase();
      const matchedByState = postcodes.find(
        (p: any) => String(p.state?.code || '').toLowerCase() === stateKey,
      );
      if (matchedByState) {
        return {
          stateCode: matchedByState?.state?.code || null,
          cityCode: matchedByState?.city?.code || null,
          cityName: matchedByState?.city?.name || null
        };
      }
    }
    
    // 매칭되는 것이 없거나 하나만 있으면 첫 번째 결과 사용
    const firstPostcode = postcodes[0] as any;
    
    return {
      stateCode: firstPostcode?.state?.code || null,
      cityCode: firstPostcode?.city?.code || null,
      cityName: firstPostcode?.city?.name || null
    };
  } catch (error) {
    console.error('❌ [findStateAndCityByPostcode] State/City 찾기 실패:', error);
    return null;
  }
};

// State 선택 시 City 목록 로드
watch(() => myAddressData.value.state, async (newState, oldState) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // 초기 바인딩: City 옵션만 로드 (기존 city/postcode 유지)
  if (oldState === undefined && newState) {
    await loadCitiesByState(newState);
    return;
  }
  
  if (newState !== oldState) {
    myAddressData.value.city = '';
    myAddressData.value.postcode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    
    if (newState) {
      await loadCitiesByState(newState);
    } else {
      cityOptions.value = [];
      cityOptionsForStateCode.value = '';
      await loadAllPostcodeOptions();
    }
  }
});

// City 선택 시 Postcode 목록 로드
watch(() => myAddressData.value.city, async (newCity, oldCity) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // 초기 바인딩: Postcode 값 유지하고 City별 Postcode 옵션만 로드
  if (oldCity === undefined && newCity) {
    await loadPostcodeOptions(newCity, true);
    return;
  }
  
  if (newCity !== oldCity) {
    myAddressData.value.postcode = '';
    postcodeOptions.value = [];
    postcodeOptionsForCityCode.value = '';
    
    if (newCity) {
      await loadPostcodeOptions(newCity, false);
    } else {
      if (postcodeAllOptionsCache.value.length === 0) {
        await loadAllPostcodeOptions();
      }
    }
  }
});

// Postcode 선택 시 State와 City 자동 선택 (역방향 조회)
watch(() => myAddressData.value.postcode, async (newPostcode, oldPostcode) => {
  if (isInitializingLocation.value || suppressLocationCascade.value) {
    return;
  }
  
  // Postcode가 입력되었을 때만 역방향 조회 수행 (지워지면 State/City 유지)
  if (newPostcode !== oldPostcode && newPostcode) {
    // Postcode 옵션이 비어있으면 먼저 로드
    if (
        postcodeAllOptionsCache.value.length === 0 &&
        postcodeOptions.value.length === 0
    ) {
      await loadAllPostcodeOptions();
    }
    
    const postcodeNumber =
        mergedResidentialPostcodeAutocompleteItems.value.find(
            (opt) => opt.TOTAL_CODE === newPostcode || opt.CODE_NAME === newPostcode
        )?.CODE_NAME || (/^[0-9a-f-]{36}$/i.test(String(newPostcode)) ? '' : String(newPostcode));
    if (!postcodeNumber) return;
    
    const locationInfo = await findStateAndCityByPostcode(
      postcodeNumber,
      myAddressData.value.state || null,
      myAddressData.value.city || null
    );
    
    if (locationInfo && locationInfo.stateCode && locationInfo.cityCode) {
      suppressLocationCascade.value = true;
      isInitializingLocation.value = true;
      
      try {
        myAddressData.value.state = locationInfo.stateCode;
        await loadCitiesByState(locationInfo.stateCode);
        myAddressData.value.city = locationInfo.cityCode;
        await loadPostcodeOptions(locationInfo.cityCode, true);
        myAddressData.value.postcode = newPostcode;
      } finally {
        await nextTick();
        suppressLocationCascade.value = false;
        isInitializingLocation.value = false;
      }
    }
  }
});

// MY 주소 데이터 동기화 플래그 (무한 루프 방지)
const isSyncingMyAddress = ref(false);

// MY 주소 데이터 변경 시 addressData 업데이트
watch(() => myAddressData.value, (newValue) => {
  if (props.useBranchAddresses) return;
  if (isSyncingMyAddress.value) return;
  if (isJurisdictionMY.value && addressData.value.addressType === 'B07001') {
    isSyncingMyAddress.value = true;
    try {
      const updatedAddressData = {
        ...addressData.value,
        localAddress: buildMyLocalAddressFromMyAddressData() as AddressInfo,
      };
      emit('update:modelValue', updatedAddressData);
    } finally {
      nextTick(() => {
        isSyncingMyAddress.value = false;
      });
    }
  }
}, { deep: true });

// addressData 변경 시 MY 주소 데이터 동기화
watch(
  () => addressData.value.localAddress,
  async (newValue) => {
  if (props.useBranchAddresses) return;
  if (isSyncingMyAddress.value) return;
  if (isResettingLocationFromCountryChange.value) return;
  if (isJurisdictionMY.value && newValue) {
    isSyncingMyAddress.value = true;
    try {
      // 초기 바인딩 중 watch들이 postcode를 지우지 않도록 보호
      isInitializingLocation.value = true;

      // readonly: 분기 유지 — 옵션 로드 + countryCode 우선, 누락 시 옵션 보강
      if (readonly.value) {
        await loadCountryOptions();
        await loadStateOptions();
        const nv = newValue as any;
        const countryCode = resolveTotalCodeFromField(
            nv.countryCode || newValue.country,
            countryOptions.value
        );
        const stateCode = resolveTotalCodeFromField(
            nv.stateCode || newValue.state,
            stateOptions.value
        );
        const cityCode = resolveTotalCodeFromField(
            nv.cityCode || newValue.city,
            cityOptions.value
        );
        if (stateCode) {
          await loadCitiesByState(stateCode);
        }
        if (cityCode && String(cityCode).trim()) {
          await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
        }
        const postcodeCode = await bindResidentialPostcodeFromLocal(
            newValue as Record<string, unknown>,
            cityCode,
        );
        if (countryCode) {
          const exists = countryOptions.value.some((opt) => opt.TOTAL_CODE === countryCode);
          if (!exists) {
            countryOptions.value = upsertOption(countryOptions.value, {
              TOTAL_CODE: String(countryCode).trim(),
              CODE_NAME: String(newValue.country || countryCode).trim().toUpperCase(),
            });
          }
        }
        myAddressData.value = {
          addressLine1: newValue.addressLine1 || '',
          addressLine2: newValue.addressLine2 || '',
          addressLine3: newValue.addressLine3 || '',
          country: countryCode,
          state: stateCode,
          city: cityCode,
          postcode: postcodeCode,
        };
        syncResidentialCountrySnapshotFromMyData();
      } else {
        if (myAddressDataMatchesLocalAddress(newValue)) {
          syncResidentialCountrySearchFromModel();
          syncResidentialCountrySnapshotFromMyData();
          return;
        }
        if (countryOptions.value.length === 0) {
          await loadCountryOptions();
        }
        if (stateOptions.value.length === 0) {
          await loadStateOptions();
        }
        const countryCode = resolveTotalCodeFromField(
            newValue.countryCode || newValue.country,
            countryOptions.value
        );
        const stateCode = resolveTotalCodeFromField(
            newValue.stateCode || newValue.state,
            stateOptions.value
        );
        const cityCode = resolveTotalCodeFromField(
            newValue.cityCode || newValue.city,
            cityOptions.value
        );
        if (stateCode) {
          await loadCitiesByState(stateCode);
        }
        if (cityCode && String(cityCode).trim()) {
          await loadPostcodeOptions(String(cityCode).trim(), true).catch(() => {});
        }
        const postcodeCode = await bindResidentialPostcodeFromLocal(
            newValue as Record<string, unknown>,
            cityCode,
        );

        myAddressData.value = {
          addressLine1: newValue.addressLine1 || '',
          addressLine2: newValue.addressLine2 || '',
          addressLine3: newValue.addressLine3 || '',
          country: countryCode,
          state: stateCode,
          city: cityCode,
          postcode: postcodeCode,
        };
        syncResidentialCountrySearchFromModel();
        syncResidentialCountrySnapshotFromMyData();
      }
    } finally {
      nextTick(() => {
        setTimeout(() => {
          isInitializingLocation.value = false;
        }, 200);
        isSyncingMyAddress.value = false;
      });
    }
  }
}, { deep: true });

const updateAddressType = (value: string | null | undefined) => {
    const normalized: AddressType = value === 'B07002' ? 'B07002' : 'B07001';

    const currentValue: AddressModel = props.modelValue || {
        addressType: normalized,
        localAddress: addressData.value.localAddress,
        foreignAddress: addressData.value.foreignAddress
    };

    emit('update:modelValue', {
        ...currentValue,
        addressType: normalized,
        localAddress: currentValue.localAddress || addressData.value.localAddress,
        foreignAddress: currentValue.foreignAddress || addressData.value.foreignAddress
    });
};
</script>

<template>
    <v-card class="contact-address-section" v-bind="$attrs">
        <v-card-title class="contact-section-title d-flex justify-space-between align-center">
            <div
                class="d-flex align-center ga-1 flex-wrap"
                :class="{ 'contact-section-title--collapsible': useContactListAddressSectionCollapse }"
                @click="toggleAddressSectionExpanded"
            >
                <v-icon
                    v-if="useContactListAddressSectionCollapse"
                    size="20"
                    class="flex-shrink-0"
                >
                    {{ addressSectionExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
                <span>{{ residentialAddressLabel }}</span>
            </div>
            <div
                v-show="!isAddressSectionCollapsed"
                class="d-flex align-center ga-2 flex-wrap"
            >
                <v-btn
                    v-show="showResidentialAddPlaceholder && canEditMyAddress"
                    size="small"
                    variant="text"
                    color="primary"
                    @click="openResidentialMyAdd"
                >
                    <v-icon start size="16">mdi-plus</v-icon>
                    {{ t('contact.Add') || 'Add' }}
                </v-btn>
                <v-btn
                    v-if="contactCdForSelect && showMyAddressEditor && !props.hideAddressConsolidationUi"
                    size="small"
                    variant="outlined"
                    color="primary"
                    @click="showAddressSelectDialog = true"
                >
                    <v-icon start size="16">mdi-map-marker-check</v-icon>
                    {{ t('contact.Select Address') || 'Select Address' }}
                </v-btn>
                <v-btn
                    v-if="isJurisdictionMY && hasMergedMyAddress && !showBranchResidentialSection"
                    color="primary"
                    variant="outlined"
                    class="sub-button"
                    :disabled="!canEditMyAddress"
                    @click="toggleMyAddressEdit"
                >
                    {{ isMyAddressEditing ? (t('common.cancel') || 'Cancel') : (t('common.edit') || 'Edit') }}
                </v-btn>
            </div>
        </v-card-title>
        <v-expand-transition>
            <v-card-text v-show="!useContactListAddressSectionCollapse || addressSectionExpanded">
            <!-- MY가 아닌 경우에만 라디오 버튼 표시 -->
            <v-radio-group
                v-if="!isJurisdictionMY"
                :model-value="addressData.addressType"
                :readonly="readonly"
                class="mb-4"
                inline
                @update:model-value="updateAddressType"
            >
                <v-radio 
                    :label="props.isIndividual ? residentialAddressLabel : (readonly ? residentialAddressLabel : (t('contact.Local Address') || 'Local Address'))" 
                    value="B07001" 
                />
                <v-radio 
                    :label="props.isIndividual ? (t('contact.Service Address') || 'Service Address') : (readonly ? (t('contact.Service Address') || 'Service Address') : (t('contact.Foreign Address') || 'Foreign Address'))" 
                    value="B07002" 
                />
            </v-radio-group>

            <!-- MY인 경우 항상 Local Address 필드 표시 -->
            <template v-if="showMyCorporateBranchAddressUi || addressData.addressType === 'B07001'">
                <template v-if="showMyCorporateBranchAddressUi">
                    <div v-if="showBranchResidentialSection" class="mb-4">
                        <div class="d-flex justify-end align-center ga-1 flex-wrap mb-3">
                            <v-spacer />
                            <v-btn
                                v-show="canEditMyAddress"
                                size="small"
                                variant="text"
                                color="primary"
                                @click="addBranchResidentialRow"
                            >
                                <v-icon start size="16">mdi-plus</v-icon>
                                {{ t('contact.Add') || 'Add' }}
                            </v-btn>
                            <v-btn
                                v-show="showConsolidateAddressLink"
                                size="small"
                                variant="text"
                                color="primary"
                                @click="consolidateDialogOpen = true"
                            >
                                <v-icon start size="16">mdi-merge</v-icon>
                                {{ t('contact.Consolidate') || 'Consolidate' }}
                            </v-btn>
                        </div>
                        <div
                            v-for="(address, index) in residentialBranchDraftList"
                            :key="`branch-residential-${index}`"
                            class="mb-3"
                        >
                            <ContactResidentialBranchRowEditor
                                :address="address"
                                :index="index"
                                :is-editing="isResidentialBranchEditing(index)"
                                :can-edit="canEditMyAddress"
                                :can-delete="canDeleteBranchResidentialRow(address)"
                                :register-address-card-style="!!props.registerAddressDeleteContext"
                                :summary-title="residentialAddressLabel"
                                :show-show-address-button="props.useBranchAddresses && !props.hideAddressConsolidationUi && isResidentialBranchEditing(index)"
                                :show-multi-hint="index === 0 && showResidentialMultiHintEffective"
                                :multi-hint-text="props.residentialMultiHintText"
                                :show-location-fields="!!branchResidentialRowShowLocationFields[index]"
                                :delete-loading="isDeleting(`residential-${index}`)"
                                :country-options="countryOptions"
                                :state-options="stateOptions"
                                :city-options="cityOptions"
                                :postcode-items="getBranchResidentialPostcodeComboItems(address)"
                                :is-loading-cities="isLoadingCities"
                                :is-loading-postcodes="isLoadingPostcodes"
                                :labels="branchRowEditorLabels"
                                @toggle-edit="toggleResidentialBranchEdit(index)"
                                @remove="removeBranchResidentialRow(index)"
                                @open-show-address="openBranchResidentialShowAddress(index)"
                                @country-focus="onBranchResidentialCountryFocus(address)"
                                @country-select="onBranchResidentialCountrySelect(address, $event)"
                                @state-change="queueBranchResidentialState(address, $event)"
                                @city-change="queueBranchResidentialCity(address, $event)"
                                @city-focus="ensureCitiesLoadedForBranchRow(address)"
                                @postcode-menu="onBranchResidentialPostcodeMenu(address, $event)"
                                @postcode-change="() => queueBranchResidentialPostcode(address)"
                            />
                        </div>
                        <v-alert
                            v-if="showBranchResidentialEmptyPlaceholder"
                            type="info"
                            variant="tonal"
                            density="comfortable"
                            class="mb-4"
                        >
                            -
                        </v-alert>
                    </div>

                    <template v-if="!showBranchResidentialSection">
                    <v-alert
                        v-show="showMyAddressSummary"
                        type="info"
                        variant="tonal"
                        density="comfortable"
                        class="mb-4"
                    >
                        <div class="d-flex justify-space-between align-center flex-wrap gap-2">
                            <div class="d-flex align-center flex-wrap flex-grow-1">
                                <span class="flex-grow-1 text-wrap">{{ mergedMyAddress }}</span>
                                <v-chip
                                    v-if="isResidentialAddressSync"
                                    color="primary"
                                    variant="flat"
                                    size="small"
                                    class="ml-2 flex-shrink-0"
                                >
                                    {{ t('contact.Sync') || 'Sync' }}
                                </v-chip>
                            </div>
                            <div
                                v-if="showResidentialMultiHintEffective"
                                class="d-flex align-center ga-2 flex-wrap"
                            >
                                <div class="d-flex align-center ga-1 flex-wrap text-right">
                                    <v-icon size="18" color="warning" icon="mdi-alert-circle-outline" />
                                    <span class="text-caption text-medium-emphasis">{{ props.residentialMultiHintText }}</span>
                                </div>
                                <v-btn
                                    v-if="props.onReSyncAddressClick"
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="pill"
                                    prepend-icon="mdi-sync"
                                    class="resync-action-btn"
                                    @click="props.onReSyncAddressClick()"
                                >
                                    Address Consolidation
                                </v-btn>
                            </div>
                        </div>
                    </v-alert>

                    <v-alert
                        v-show="showResidentialAddPlaceholder || showMyAddressReadonlyEmpty"
                        type="info"
                        variant="tonal"
                        density="comfortable"
                        class="mb-4"
                    >
                        <div class="d-flex justify-end align-center flex-wrap gap-2">
                            <div class="d-flex align-center ga-1 flex-wrap text-right">
                                <v-icon
                                    v-if="showResidentialMultiHintEffective"
                                    size="18"
                                    color="warning"
                                    icon="mdi-alert-circle-outline"
                                />
                                <span class="text-caption text-medium-emphasis" v-if="showResidentialMultiHintEffective">
                                    {{ props.residentialMultiHintText }}
                                </span>
                                <span v-else>-</span>
                            </div>
                            <v-btn
                                v-if="showResidentialMultiHintEffective && props.onReSyncAddressClick"
                                size="small"
                                variant="tonal"
                                color="primary"
                                rounded="pill"
                                prepend-icon="mdi-sync"
                                class="resync-action-btn"
                                @click="props.onReSyncAddressClick()"
                            >
                                Address Consolidation
                            </v-btn>
                        </div>
                    </v-alert>

                    <div v-show="showMyAddressEditor">
                    <div
                        v-if="!hasMergedMyAddress && residentialMyExpanded"
                        class="d-flex justify-end mb-2"
                    >
                        <v-btn
                            size="small"
                            variant="text"
                            color="primary"
                            @click="cancelResidentialMyDraft"
                        >
                            <v-icon start size="16">mdi-close</v-icon>
                            {{ t('contact.Cancel') || 'Cancel' }}
                        </v-btn>
                    </div>
                    <!-- Address Line 1 -->
                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field 
                                v-model="myAddressData.addressLine1" 
                                :label="t('contact.Address Line 1') || 'Address Line 1'" 
                                :readonly="readonly"
                                :class="{ 'readonly-field': readonly }"
                                variant="outlined" 
                                density="comfortable" 
                                hide-details 
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- Address Line 2 -->
                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field 
                                v-model="myAddressData.addressLine2" 
                                :label="t('contact.Address Line 2') || 'Address Line 2'" 
                                :readonly="readonly"
                                :class="{ 'readonly-field': readonly }"
                                variant="outlined" 
                                density="comfortable" 
                                hide-details 
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- Address Line 3 -->
                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field 
                                v-model="myAddressData.addressLine3" 
                                :label="t('contact.Address Line 3') || 'Address Line 3'" 
                                :readonly="readonly"
                                :class="{ 'readonly-field': readonly }"
                                variant="outlined" 
                                density="comfortable" 
                                hide-details 
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- Country -->
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <!-- Readonly일 때는 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="readonly"
                                :model-value="getCountryDisplayName"
                                :label="t('contact.Country') || 'Country'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-autocomplete
                                v-else
                                v-model="myAddressData.country"
                                v-model:search="countrySearch"
                                :items="countryOptions"
                                :label="t('contact.Country') || 'Country'"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                variant="outlined"
                                density="comfortable"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                hide-details
                                clearable
                                :auto-select-first="false"
                                @focus="onResidentialCountryFocus"
                                @update:model-value="onMyResidentialCountryUpdate"
                            />
                        </v-col>
                        
                        <!-- State -->
                        <v-col v-if="shouldShowLocationFields" cols="12" md="6">
                            <!-- Readonly일 때는 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="readonly"
                                :model-value="getStateDisplayName"
                                :label="t('contact.State') || 'State'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-autocomplete
                                v-else
                                v-model="myAddressData.state"
                                :items="stateOptions"
                                :label="t('contact.State') || 'State'"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                variant="outlined"
                                density="comfortable"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                hide-details
                                clearable
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- City, Postcode -->
                    <v-row v-if="shouldShowLocationFields" dense>
                        <v-col cols="12" md="6">
                            <!-- Readonly일 때는 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="readonly"
                                :model-value="getCityDisplayName"
                                :label="t('contact.City') || 'City'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-autocomplete
                                v-else
                                v-model="myAddressData.city"
                                :items="cityOptions"
                                :label="t('contact.City') || 'City'"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                variant="outlined"
                                density="comfortable"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                hide-details
                                clearable
                                :loading="isLoadingCities"
                            />
                        </v-col>
                        
                        <v-col cols="12" md="6">
                            <!-- Readonly일 때는 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="readonly"
                                :model-value="getPostcodeDisplayName"
                                :label="t('contact.Postcode') || 'Postcode'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <v-autocomplete
                                v-else
                                v-model="myAddressData.postcode"
                                :items="postcodeOptions"
                                :label="t('contact.Postcode') || 'Postcode'"
                                item-title="CODE_NAME"
                                item-value="TOTAL_CODE"
                                variant="outlined"
                                density="comfortable"
                                :placeholder="t('contact.Please Select') || 'Please Select'"
                                hide-details
                                clearable
                                :loading="isLoadingPostcodes"
                                @update:menu="$event && void ensureResidentialPostcodeFullCacheForReverse()"
                            />
                        </v-col>
                    </v-row>
                    </div>
                    </template>
                </template>
                
                <!-- MY가 아닌 경우 기존 필드 -->
                <template v-else>
                    <!-- 첫 번째 줄: 우편번호만 -->
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <!-- Readonly일 때는 일반 텍스트 필드로 표시 -->
                            <v-text-field
                                v-if="isFieldReadonly('localAddress.postalCode')"
                                :model-value="addressData.localAddress!.postalCode"
                                :label="t('contact.Postal Code') || 'Postal Code'"
                                :readonly="true"
                                :class="{ 'readonly-field': true }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                            <!-- 수정 가능할 때는 일반 필드 -->
                            <v-text-field
                                v-else
                                v-model="addressData.localAddress!.postalCode"
                                :label="t('contact.Postal Code') || 'Postal Code'"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- 두 번째 줄: Block/House No, Street Name, Building/Estate Name (모두 수정 가능) -->
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.blockHouseNo"
                                :label="t('contact.Block/House No') || 'Block/House No'"
                                :readonly="isFieldReadonly('localAddress.blockHouseNo')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.blockHouseNo') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                        
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.streetName"
                                :label="t('contact.Street Name') || 'Street Name'"
                                :readonly="isFieldReadonly('localAddress.streetName')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.streetName') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                        
                        <v-col cols="12" md="4">
                            <v-text-field
                                v-model="addressData.localAddress!.buildingEstateName"
                                :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                                :readonly="isFieldReadonly('localAddress.buildingEstateName')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.buildingEstateName') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                    
                    <!-- 세 번째 줄: Level, Unit (수정 가능) -->
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="addressData.localAddress!.level"
                                :label="t('contact.Level') || 'Level'"
                                :readonly="isFieldReadonly('localAddress.level')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.level') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                        
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="addressData.localAddress!.unit"
                                :label="t('contact.Unit') || 'Unit'"
                                :readonly="isFieldReadonly('localAddress.unit')"
                                :class="{ 'readonly-field': isFieldReadonly('localAddress.unit') }"
                                variant="outlined"
                                density="comfortable"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                </template>
            </template>

            <template v-else>
                <v-row dense>
                    <v-col cols="12">
                        <v-text-field
                            v-model="addressData.foreignAddress!.addressLine1"
                            :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                            :readonly="isFieldReadonly('foreignAddress.addressLine1')"
                            :class="{ 'readonly-field': isFieldReadonly('foreignAddress.addressLine1') }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                        />
                    </v-col>
                    
                    <v-col cols="12">
                        <v-text-field
                            v-model="addressData.foreignAddress!.addressLine2"
                            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                            :readonly="isFieldReadonly('foreignAddress.addressLine2')"
                            :class="{ 'readonly-field': isFieldReadonly('foreignAddress.addressLine2') }"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                        />
                    </v-col>
                </v-row>
            </template>
        </v-card-text>
        </v-expand-transition>
    </v-card>

    <ContactAddressSelectDialog
        v-if="contactCdForSelect"
        v-model="showAddressSelectDialog"
        :contact-cd="contactCdForSelect"
        filter-address-type="M04001"
        @select="onAddressCandidateSelected"
    />

    <ContactBranchAddressShowDialog
        v-model="showBranchResidentialShowDialog"
        :items="branchResidentialShowItems"
        @select="onBranchResidentialShowSelected"
    />

    <ContactAddressConsolidateDialog
        v-if="String(props.contactCd || '').trim()"
        v-model="consolidateDialogOpen"
        :contact-cd="String(props.contactCd || '').trim()"
        @consolidated="onAddressConsolidated"
    />
</template>

<style scoped lang="scss">
/* Readonly 필드 스타일 (회색 배경) */
.readonly-field {
    opacity: 1 !important;
}

.readonly-field :deep(.v-field) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-input__control) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(.v-field__overlay) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
}

.readonly-field :deep(input),
.readonly-field :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field :deep(.v-field--variant-outlined .v-field__input) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
}

.readonly-field.v-text-field--disabled {
    opacity: 1 !important;
}

.readonly-field.v-text-field--disabled :deep(.v-field),
.readonly-field.v-text-field--disabled :deep(.v-field__input),
.readonly-field.v-text-field--disabled :deep(.v-input__control),
.readonly-field.v-text-field--disabled :deep(.v-field__overlay),
.readonly-field.v-text-field--disabled :deep(input),
.readonly-field.v-text-field--disabled :deep(textarea) {
    background-color: #F5F5F5 !important;
    background: #F5F5F5 !important;
    opacity: 1 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    cursor: not-allowed !important;
    -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}
.contact-address-section {
    background-color: rgb(var(--v-theme-surface)) !important;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 24px;
}

/* 폰트 패밀리 */
* {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.contact-section-title {
    font-size: 18px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    padding: 16px 24px;
}

.contact-section-title--collapsible {
    cursor: pointer;
    user-select: none;
}

.contact-address-section :deep(.v-card-text) {
    padding: 24px !important;
}

.resync-action-btn {
    text-transform: none;
    font-weight: 600;
}
</style>

